import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { X } from 'lucide-react';
import LangEnum from '../../../lang/LangEnum';
import { MediaFormatEnum } from '../../../modules/general-settings/enums/mediaFormat.enum';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import ProductsAddedCardItem from './components/ProductsAddedCardItem';
import {
  PRODUCTS_ADDED_CARD_HEIGHT,
  PRODUCTS_ADDED_STACK_HEIGHT,
  useProductsAddedCardStyles,
} from './productsAddedCard.styles';
import { ProductsAddedCardItem as ProductsAddedCardItemData } from './productsAddedCard.types';

const formatExtraItemsCount = (count: number) => (count > 0 ? ` +${count}` : '');

interface ProductsAddedCardProps {
  items: ProductsAddedCardItemData[];
  mediaFormat: MediaFormatEnum;
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  onUndo: () => void;
  onClose: () => void;
}

const ProductsAddedCard = ({
  items,
  mediaFormat,
  isExpanded,
  onExpandedChange,
  onUndo,
  onClose,
}: ProductsAddedCardProps) => {
  const { translate } = useTranslation();
  const { classes, cx } = useProductsAddedCardStyles({ mediaFormat });
  const extraItemsCount = Math.max(items.length - 1, 0);
  const hasManyItems = extraItemsCount > 0;
  const isListOpen = hasManyItems && isExpanded;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, paddingBottom: 1 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }}>
          {translate(LangEnum.PRODUCT_NOTIFICATION_ADD)}
          {formatExtraItemsCount(extraItemsCount)}
        </Typography>

        <Icon
          icon={X}
          className={classes.closeIcon}
          ariaLabel={translate(LangEnum.GLOBAL_CLOSE)}
          onClick={onClose}
        />
      </Stack>

      {isListOpen ? (
        <Stack className={classes.expandedList}>
          {items.map((item, index) => (
            <ProductsAddedCardItem
              key={`${item.code}-${item.colorName}-${index}`}
              item={item}
              mediaFormat={mediaFormat}
              height={PRODUCTS_ADDED_CARD_HEIGHT[mediaFormat]}
            />
          ))}
        </Stack>
      ) : (
        <Box
          className={cx({
            [classes.cardStack]: hasManyItems,
            [classes.clickableStack]: hasManyItems,
          })}
          onClick={hasManyItems ? () => onExpandedChange(true) : undefined}
        >
          <ProductsAddedCardItem
            item={items[0]}
            mediaFormat={mediaFormat}
            height={PRODUCTS_ADDED_CARD_HEIGHT[mediaFormat]}
          />
        </Box>
      )}

      <Stack
        direction="row"
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 0.5,
          paddingTop: 1,
          marginTop: hasManyItems && !isListOpen ? `${PRODUCTS_ADDED_STACK_HEIGHT}px` : 0,
        }}
      >
        {hasManyItems && (
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={() => onExpandedChange(!isExpanded)}
          >
            {translate(isExpanded ? LangEnum.GLOBAL_COLLAPSE : LangEnum.GLOBAL_SEE_ALL)}
          </Button>
        )}
        <Button variant="text" color="primary" size="small" onClick={onUndo}>
          {translate(LangEnum.PRODUCT_NOTIFICATION_UNDO)}
        </Button>
      </Stack>
    </Box>
  );
};

export default ProductsAddedCard;
